import JSZip from "jszip";

export const ESCAPE_REGEX_CHARS = /[.*+?^${}()|[\]\\]/g;

export interface ReplaceOptions {
  find: string;
  replace: string;
  useRegex: boolean;
  caseSensitive: boolean;
  /** Match whole words only ("cat" but not "catalog"). */
  wholeWord?: boolean;
  /** Replacement follows the case of what it replaces (Cat -> Dog, CAT -> DOG). */
  preserveCase?: boolean;
  /** Regex mode: ^ and $ match at every line, not only at the start and end of the text. */
  multiline?: boolean;
}

/** Builds the regex used for both matching and replacing, so every part of
 * this tool (the match counter, plain-text replace, and DOCX in-place
 * replace) applies find/replace/case/regex settings identically -- one
 * source of truth instead of three places that could quietly drift apart. */
export function buildReplacePattern(opts: Pick<ReplaceOptions, "find" | "useRegex" | "caseSensitive" | "wholeWord" | "multiline">): RegExp {
  let pattern = opts.useRegex ? opts.find : opts.find.replace(ESCAPE_REGEX_CHARS, "\\$&");
  if (opts.wholeWord) pattern = `(?<![\\p{L}\\p{N}_])(?:${pattern})(?![\\p{L}\\p{N}_])`;
  const flags = (opts.caseSensitive ? "g" : "gi") + (opts.multiline && opts.useRegex ? "m" : "") + (opts.wholeWord ? "u" : "");
  return new RegExp(pattern, flags);
}

export function safeMatchCount(opts: ReplaceOptions, text: string): number | "invalid" {
  if (!opts.find) return 0;
  try {
    return (text.match(buildReplacePattern(opts)) || []).length;
  } catch {
    return "invalid";
  }
}

/** Expands $&, $1, $<name>, $$ in a replacement template the way String.replace does. */
function expandTemplate(template: string, m: string, groups: string[], named: Record<string, string> | undefined, input: string, offset: number): string {
  return template.replace(/\$(\$|&|`|'|\d{1,2}|<[^>]+>)/g, (_x, tok: string) => {
    if (tok === "$") return "$";
    if (tok === "&") return m;
    if (tok === "`") return input.slice(0, offset);
    if (tok === "'") return input.slice(offset + m.length);
    if (tok[0] === "<") return named?.[tok.slice(1, -1)] ?? "";
    const n = Number(tok);
    return n >= 1 && n <= groups.length ? groups[n - 1] ?? "" : "$" + tok;
  });
}

function applyCase(original: string, replacement: string): string {
  if (!/[A-Za-z\u00C0-\u024F]/.test(original)) return replacement;
  if (original === original.toUpperCase() && original.length > 1) return replacement.toUpperCase();
  if (original === original.toLowerCase()) return replacement;
  if (original[0] === original[0].toUpperCase() && original.slice(1) === original.slice(1).toLowerCase()) return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  return replacement;
}

/** What one match turns into. In normal (non-regex) mode the replacement is literal text, so "$&" or "$1"
 * stay as typed. In regex mode $1, $& and $<name> work, and \n and \t are line breaks and tabs. */
function replacementFor(opts: ReplaceOptions, args: any[]): string {
  const m = args[0] as string;
  const hasNamed = typeof args[args.length - 1] === "object" && args[args.length - 1] !== null;
  const named = hasNamed ? (args[args.length - 1] as Record<string, string>) : undefined;
  const rest = hasNamed ? args.slice(1, -1) : args.slice(1);
  const input = rest[rest.length - 1] as string;
  const offset = rest[rest.length - 2] as number;
  const groups = rest.slice(0, -2) as string[];
  let out: string;
  if (opts.useRegex) {
    const template = opts.replace.replace(/\\n/g, "\n").replace(/\\t/g, "\t");
    out = expandTemplate(template, m, groups, named, input, offset);
  } else {
    out = opts.replace;
  }
  return opts.preserveCase ? applyCase(m, out) : out;
}

export interface TextMatch { start: number; end: number; replacement: string }

/** Every match in `text` with its replacement, without changing anything. */
export function findMatches(opts: ReplaceOptions, text: string): TextMatch[] {
  const found: TextMatch[] = [];
  text.replace(buildReplacePattern(opts), (...args: any[]) => {
    const m = args[0] as string;
    const hasNamed = typeof args[args.length - 1] === "object" && args[args.length - 1] !== null;
    const rest = hasNamed ? args.slice(1, -1) : args.slice(1);
    const offset = rest[rest.length - 2] as number;
    found.push({ start: offset, end: offset + m.length, replacement: replacementFor(opts, args) });
    return m;
  });
  return found;
}

/** Plain string/regex replace, used for .txt/.json/.csv/.md/.html/.xml --
 * anything that's just text. */
export function replaceInText(opts: ReplaceOptions, text: string): string {
  return text.replace(buildReplacePattern(opts), (...args: any[]) => replacementFor(opts, args));
}

// ---- DOCX in-place editing ----
//
// A .docx is a ZIP archive; its visible text lives inside word/document.xml
// as a series of <w:t> ("text") elements, each belonging to a <w:r> ("run")
// that carries that run's own formatting (bold, italic, color, etc.).
// Editing each <w:t> node's text directly and re-saving the same archive
// preserves every run's formatting, every image, and the whole document
// structure untouched -- instead of extracting plain text and generating a
// brand new, unformatted document from scratch.
//
// Honest, unavoidable limitation: if a single visible word is split across
// two separate <w:t> nodes (common wherever formatting changes mid-word,
// or from how some versions of Word save runs), a search term spanning
// that exact boundary won't be found, since each node is searched on its
// own. This is a real constraint of every tool that edits a docx this way,
// not something specific to this implementation -- disclosed directly in
// the tool's own description rather than glossed over.

export interface DocxEditResult {
  blob: Blob;
  replacementCount: number;
  previewText: string;
}

async function loadDocxXml(file: File): Promise<{ zip: JSZip; xmlDoc: Document; xmlPath: string }> {
  const zip = await JSZip.loadAsync(file);
  const xmlPath = "word/document.xml";
  const xmlFile = zip.file(xmlPath);
  if (!xmlFile) throw new Error("This doesn't look like a valid .docx file (missing word/document.xml).");
  const xmlText = await xmlFile.async("text");
  const xmlDoc = new DOMParser().parseFromString(xmlText, "application/xml");
  const parseError = xmlDoc.getElementsByTagName("parsererror")[0];
  if (parseError) throw new Error("This .docx file's internal structure couldn't be read.");
  return { zip, xmlDoc, xmlPath };
}

function extractDocxPreviewText(xmlDoc: Document): string {
  const paragraphs = Array.from(xmlDoc.getElementsByTagName("w:p"));
  return paragraphs
    .map((p) => Array.from(p.getElementsByTagName("w:t")).map((n) => n.textContent || "").join(""))
    .join("\n");
}

/** Reads a .docx and returns its visible text as a read-only preview --
 * used to show what's in the file before editing, without touching it. */
export async function docxToPreviewText(file: File): Promise<string> {
  const { xmlDoc } = await loadDocxXml(file);
  return extractDocxPreviewText(xmlDoc);
}

/** Applies a find/replace to a .docx file's actual text in place, keeping all formatting. Each paragraph is
 * searched as one piece of text, so a phrase is found even where Word has split it into several runs
 * (bold/italic changes, spell-check marks, revision ids). The replacement goes into the run where the match
 * starts (keeping that run's formatting) and the matched text is removed from any later runs. */
export async function docxReplaceInPlace(file: File, opts: ReplaceOptions): Promise<DocxEditResult> {
  const { zip, xmlDoc, xmlPath } = await loadDocxXml(file);
  buildReplacePattern(opts); // throws for an invalid regex before anything is touched
  let replacementCount = 0;

  for (const para of Array.from(xmlDoc.getElementsByTagName("w:p"))) {
    const nodes = Array.from(para.getElementsByTagName("w:t"));
    if (nodes.length === 0) continue;
    const texts = nodes.map((n) => n.textContent || "");
    const full = texts.join("");
    if (!full) continue;
    const matches = findMatches(opts, full);
    if (matches.length === 0) continue;
    replacementCount += matches.length;

    // Character range of each node within the paragraph text.
    const ranges: Array<[number, number]> = [];
    let pos = 0;
    for (const t of texts) { ranges.push([pos, pos + t.length]); pos += t.length; }

    nodes.forEach((node, idx) => {
      const [a, b] = ranges[idx];
      let out = "";
      let cursor = a;
      for (const m of matches) {
        // The node the match starts in (or, for an empty match at the very end, the last node) owns the replacement.
        const startsHere = (m.start >= a && m.start < b) || (m.start === b && idx === nodes.length - 1 && m.end === m.start);
        const overlapStart = Math.max(m.start, a), overlapEnd = Math.min(m.end, b);
        if (!startsHere && overlapEnd <= overlapStart) continue;
        if (overlapStart > cursor) out += texts[idx].slice(cursor - a, overlapStart - a);
        if (startsHere) out += m.replacement;
        cursor = Math.max(cursor, overlapEnd, startsHere ? m.start : 0);
      }
      if (cursor < b) out += texts[idx].slice(cursor - a);
      if (out !== texts[idx]) {
        node.textContent = out;
        node.setAttribute("xml:space", "preserve");
      }
    });
  }

  const newXml = new XMLSerializer().serializeToString(xmlDoc);
  zip.file(xmlPath, newXml);
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 }, mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });

  return { blob, replacementCount, previewText: extractDocxPreviewText(xmlDoc) };
}
